import type { JSONContent } from '@tiptap/vue-3';
import type { SocialEmbedProvider } from '~kui/utils/socialEmbed';

import { toSocialEmbed } from '~kui/utils/socialEmbed';
import { cva } from 'class-variance-authority';

export const IMAGE_ORIENTATION = {
  Landscape: 'landscape',
  Portrait: 'portrait',
} as const;

export type ImageOrientation = typeof IMAGE_ORIENTATION[keyof typeof IMAGE_ORIENTATION];

export const RICH_TEXT_MEDIA_NODE_TYPES = ['image', 'socialEmbed'] as const;

export type RichTextMediaNodeType = typeof RICH_TEXT_MEDIA_NODE_TYPES[number];

export interface RichTextImageAttrs {
  src: string;
  alt: string;
  caption: string;
  orientation: ImageOrientation;
  width: number | null;
  height: number | null;
}

export interface SocialEmbedAttrs {
  provider: string;
  id: string;
  url: string;
}

/** TikTok's player is a bare 9:16 video; Instagram's frame has no fixed ratio, so it gets a set height. */
export const socialEmbedFrameVariants = cva('w-full rounded-lg', {
  variants: {
    provider: {
      tiktok: 'aspect-9/16',
      instagram: 'h-160',
    } satisfies Record<SocialEmbedProvider, string>,
  },
});

const SAFE_IMAGE_SRC_PATTERN = /^https:\/\//i;

export const isSafeImageSrc = (src?: string | null): src is string =>
  typeof src === 'string' && SAFE_IMAGE_SRC_PATTERN.test(src.trim());

export const toImageOrientation = (value?: string | null): ImageOrientation =>
  value === IMAGE_ORIENTATION.Portrait ? IMAGE_ORIENTATION.Portrait : IMAGE_ORIENTATION.Landscape;

export const orientationFromSize = ({ width, height }: { width: number; height: number }): ImageOrientation =>
  height > width ? IMAGE_ORIENTATION.Portrait : IMAGE_ORIENTATION.Landscape;

export const isRichTextMediaNodeType = (type?: string): type is RichTextMediaNodeType =>
  (RICH_TEXT_MEDIA_NODE_TYPES as readonly string[]).includes(type ?? '');

/** A media node the renderer can draw: an https image, or an embed with a known provider and a valid id. */
export const isRenderableMediaNode = (node: JSONContent): boolean => {
  if (node.type === 'image') {
    return isSafeImageSrc(node.attrs?.src);
  }

  return node.type === 'socialEmbed' && toSocialEmbed({ provider: node.attrs?.provider, id: node.attrs?.id }) !== null;
};
